import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CourseDetailsContent } from "content";
import { useBreadcrumbs } from "hooks/redux";
import { useGroupById } from "hooks/api/groups";
import {
  useAddCourseLessonToGroup,
  useCourseById,
  useCourseLessons,
  useDetachCourseLessonFromGroup,
} from "hooks/api/courses";
import { useAddLessonToGroupColumns } from "hooks/table/useAddLessonToGroupColumns";
import { Table } from "components/Table";
import { Empty } from "components/Common/Empty";
import { CourseHero } from "./components/CourseHero";
import { Route } from "constants/routes";
import { TGroupCourseLesson } from "types/api/course";
import "./GroupClassroomCourse.styles.scss";

export const GroupClassroomCourse: React.FC = () => {
  const { groupId, courseId } = useParams();
  const navigate = useNavigate();

  const [pageNumber, setPageNumber] = useState<number>(0);

  const { setPageName, resetPageName } = useBreadcrumbs();
  const {
    data: course,
    isLoading: isCourseLoading,
    error,
  } = useCourseById(courseId);
  const { data: group } = useGroupById(groupId ? parseInt(groupId) : undefined);

  // Get all course lessons
  const { data: courseLessonsData, isLoading: isLessonsLoading } =
    useCourseLessons(courseId, {
      pageNumber,
      pageSize: 99999,
    });

  // Get group-specific lessons to determine which are pinned
  const { data: groupCourseLessonsData, isLoading: isGroupLessonsLoading } =
    useCourseLessons(courseId, {
      pageNumber,
      groupId,
    });

  const { mutate: addLessonToGroup } = useAddCourseLessonToGroup();
  const { mutate: detachCourseLessonFromGroup } =
    useDetachCourseLessonFromGroup();

  const courseLessons: TGroupCourseLesson[] = useMemo(
    () =>
      (courseLessonsData?.content || []).map((lesson) => ({
        ...lesson,
        isPinned:
          groupCourseLessonsData?.content?.some(
            (groupLesson) => groupLesson.id === lesson.id
          ) || false,
      })),
    [courseLessonsData?.content, groupCourseLessonsData?.content]
  );

  const isEmpty: boolean = useMemo(
    () => courseLessons.length === 0,
    [courseLessons]
  );

  const isLoading: boolean = useMemo(
    () => isLessonsLoading || isGroupLessonsLoading,
    [isLessonsLoading, isGroupLessonsLoading]
  );

  const pinLessonClickHandler = (courseLessonId: number) => {
    if (!groupId || !courseId) return;

    addLessonToGroup({
      courseId,
      lessonId: courseLessonId,
      body: { groupId: Number(groupId) },
    });
  };

  const unpinLessonClickHandler = (courseLessonId: number) => {
    if (!groupId || !courseId) return;

    detachCourseLessonFromGroup({
      courseId,
      lessonId: courseLessonId,
      body: { groupId: Number(groupId) },
    });
  };

  const viewLessonClickHandler = (courseLessonId: number) => {
    if (!groupId || !courseId || !group?.memberIds?.length) return;

    navigate(
      Route.App.Study.GROUP_CLASSROOM_COURSE_LESSON.construct(
        groupId,
        courseId,
        courseLessonId.toString()
      )
    );
  };

  const columns = useAddLessonToGroupColumns(courseId, {
    onPinClick: pinLessonClickHandler,
    onUnPinClick: unpinLessonClickHandler,
    onViewClick: viewLessonClickHandler,
  });

  useEffect(() => {
    if (group?.name) {
      setPageName(group.name);
    }
    return () => resetPageName();
  }, [group?.name, setPageName, resetPageName]);

  useEffect(() => {
    // TODO: handler properly not found resource error
    if (error) {
      navigate(Route.App.Materials.ROOT.path);
    }
  }, [error, navigate]);

  return (
    <>
      <CourseHero course={course} loading={isCourseLoading} />
      {isEmpty && !isLoading ? (
        <Empty text={CourseDetailsContent.EmptyForStudent} />
      ) : (
        <Table<TGroupCourseLesson>
          data={courseLessons}
          columns={columns}
          loading={isLoading}
          totalItems={courseLessonsData?.totalElements}
          onPageChange={setPageNumber}
          hideHeader
          hideFooter
        />
      )}
    </>
  );
};
